import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {buildPublicId} from '../../../../models/applications/publicIdTypes';
import {
  CacheStats,
  FetchSourcesEnum,
  PrefetchEventsEnum,
  PrefetchStats,
  TraceEvent,
  TraceEventTypesEnum,
  UITraceEvent,
} from '../../../../services/trace';
import {DropdownSeparator} from '../../../common/dropdowns/dropdownSeparator';
import {TwoLinesDropdownItem} from '../../../common/dropdowns/twoLinesDropdownItem';

/*
 * Props.
 */

export interface TraceDropdownProps extends DropdownProps {
  hasOfflineCache: boolean;
  traceEvents: ReadonlyArray<UITraceEvent>;
  cacheStats: CacheStats;
  prefetchStats: PrefetchStats;
  openEventProfile: HandlerOf<UITraceEvent>;
  isTraceEnabled: boolean;
  toggleTrace: Handler;
}

/*
 * Component.
 */

export const TraceDropdown: FC<TraceDropdownProps> = (props) => {
  const {
    hasOfflineCache,
    traceEvents,
    cacheStats,
    prefetchStats,
    isTraceEnabled,
    toggleTrace,
    openEventProfile,
  } = props;

  const itemCount = traceEvents.length + 1;

  return (
    <Dropdown {...props}>
      <DropdownHeader {...props} title={<Trans id="trace-header">Performance traces</Trans>} />

      <DropdownList {...props} itemCount={itemCount}>
        <TwoLinesDropdownItem
          title={<Trans id="trace-backend-header">Enable backend profiling</Trans>}
          description={
            <Trans id="trace-backend-description">Backend will collect low-level performance timings</Trans>
          }
          isChecked={isTraceEnabled}
          onSelect={toggleTrace}
        />

        <TwoLinesDropdownItem
          title={<Trans id="trace-cache-header">Cache stats</Trans>}
          description={renderCacheStats(hasOfflineCache, cacheStats)}
        />

        <TwoLinesDropdownItem
          title={<Trans id="trace-prefetch-header">Prefetch log</Trans>}
          description={renderPrefetchStats(hasOfflineCache, prefetchStats)}
        />

        <DropdownSeparator />

        {traceEvents.map((traceEvent) => (
          <TwoLinesDropdownItem
            key={traceEvent.id}
            title={renderEventTitle(traceEvent)}
            description={renderEventTimings(traceEvent)}
            onSelect={() => openEventProfile(traceEvent)}
          />
        ))}
      </DropdownList>
    </Dropdown>
  );
};

function renderCacheStats(isOfflineCacheEnabled: boolean, cacheStats: CacheStats) {
  if (!isOfflineCacheEnabled) {
    return 'Cache is disabled';
  }

  const {numPrefetched, numFetched, numHits} = cacheStats;

  const hitRate = numFetched === 0 ? 0 : (100 * numHits) / numFetched;

  return `prefetch:${numPrefetched} fetch:${cacheStats.numFetched} hits:${hitRate.toFixed(1)}%`;
}

function renderPrefetchStats(isOfflineCacheEnabled: boolean, prefetchStats: PrefetchStats) {
  if (!isOfflineCacheEnabled) {
    return 'Prefetcher is disabled';
  }

  return prefetchStats.log
    .map((event) => {
      switch (event.type) {
        case PrefetchEventsEnum.PREFETCH: {
          const publicId = buildPublicId('conversation', event.conversationId);
          return event.isSuccess ? publicId : `${publicId} [err]`;
        }

        case PrefetchEventsEnum.NOOP:
          if (event.count === 1) {
            return 'noop';
          }

          return `noop [${event.count}]`;

        default:
          return '';
      }
    })
    .join(', ');
}

function renderEventTitle(traceEvent: TraceEvent) {
  switch (traceEvent.type) {
    case TraceEventTypesEnum.SWITCH_CONVERSATION:
      return `View ${buildPublicId('conversation', traceEvent.event.conversationId)}`;

    case TraceEventTypesEnum.SWITCH_FOLDER:
      return `Folder ${traceEvent.event.path}`;

    case TraceEventTypesEnum.INITIAL_APP_LOAD:
      return 'Initial app load';

    default:
      return 'Unknown';
  }
}

function renderEventTimings(traceEvent: TraceEvent) {
  if (traceEvent.type === TraceEventTypesEnum.INITIAL_APP_LOAD) {
    const {appLoad, bootLoad, initialResponse} = traceEvent.event;

    return `app:${Math.round(appLoad)}ms boot:${Math.round(
      bootLoad,
    )}ms initial response:${initialResponse}ms`;
  }

  const {totalTime, idleFetchTime, busyTime, fetchInfos} = traceEvent.event;

  if (!fetchInfos) {
    return null;
  }

  const isLong = totalTime > 1000;

  switch (fetchInfos.source) {
    case FetchSourcesEnum.SERVER: {
      const {serverTime = 0} = fetchInfos;
      const networkTime = idleFetchTime - serverTime;

      return `${isLong ? '!! ' : ''}server:${Math.round(serverTime)}ms network:${Math.round(
        networkTime,
      )}ms busy:${Math.round(busyTime)}ms`;
    }

    case FetchSourcesEnum.CACHE:
      return `[cached] busy:${Math.round(busyTime)}ms`;

    default:
      return null;
  }
}
